
import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { BrandPicker } from './BrandPicker'
import { DeviceEditor } from './DeviceEditor'
import { DeviceGroupHeader } from './DeviceGroupHeader'
import { SignalChip } from './SignalChip'
import { Icon } from '@/components/ui/Icon'
import {
  CATEGORY_META,
  DEVICE_CATEGORIES,
  SLOT_BUS_LABELS,
  distinctBrands,
  groupByCategory,
  isCard,
  partitionGroups,
  slotDescriptor,
  type DeviceCategory,
} from '@/lib/categories'
import { verificationSummary } from '@/lib/format'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { buildSearchIndex, searchDevices } from '@/lib/search'
import { describeDevice, formatFormFactor, metaLine, SIGNAL_CATEGORIES } from '@/lib/signals'
import { btn, card, cx, input, inputBase, notice, segment, starBtn, tag, tagTone } from '@/lib/ui'
import { partitionPorts, splitPorts, type BrandDTO, type DeviceDTO } from '@/lib/types'
import { DENSITY_KEY, densityValue, useLocalStorage, type Density } from '@/lib/use-local-storage'
import { errorMessage } from '@/lib/client-api'

type Scope = 'workspace' | 'catalogue'
type Mode =
  | { kind: 'idle' }
  | { kind: 'create'; scope: Scope }
  | { kind: 'edit'; device: DeviceDTO }

function scopeOf(device: DeviceDTO): Scope {
  return device.workspaceId ? 'workspace' : 'catalogue'
}

const byBrandNameModel = (a: DeviceDTO, b: DeviceDTO) =>
  a.manufacturer.localeCompare(b.manufacturer) ||
  a.name.localeCompare(b.name) ||
  a.model.localeCompare(b.model)

/** Module-level so the storage hook's memo only recomputes when the value changes. */
const DEFAULT_DENSITY: Density = 'comfortable'

export function LibraryClient({
  initialDevices,
  unitCounts,
  workspaceId,
  apiBase,
  inventoryPath,
  isPlatformAdmin,
  brands,
  initialFavouriteIds,
  initialSelectedId,
  initialEdit = false,
}: {
  initialDevices: DeviceDTO[]
  unitCounts: Record<string, number>
  workspaceId: string
  apiBase: string
  inventoryPath: string
  isPlatformAdmin: boolean
  /** Brand rows (logos, websites) for the manufacturers in this list. */
  brands: BrandDTO[]
  /** Devices this workspace has starred. */
  initialFavouriteIds: string[]
  /** Deep link (?device=…) — opens on that device instead of the first row. */
  initialSelectedId?: string
  /** Deep link (&edit=1) — opens the editor straight away when allowed. */
  initialEdit?: boolean
}) {
  const { advanced } = useUiMode()
  const router = useRouter()
  const canEdit = (device: DeviceDTO) =>
    scopeOf(device) === 'workspace' ? device.workspaceId === workspaceId : isPlatformAdmin

  const [devices, setDevices] = useState(initialDevices)
  const [brandRows, setBrandRows] = useState(brands)
  const [favouriteIds, setFavouriteIds] = useState<ReadonlySet<string>>(() => new Set(initialFavouriteIds))
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const linked = initialSelectedId && initialDevices.find((device) => device.id === initialSelectedId)
    return linked ? linked.id : (initialDevices[0]?.id ?? null)
  })
  const [mode, setMode] = useState<Mode>(() => {
    const linked = initialSelectedId && initialDevices.find((device) => device.id === initialSelectedId)
    return initialEdit && linked && canEdit(linked) ? { kind: 'edit', device: linked } : { kind: 'idle' }
  })
  const [query, setQuery] = useState('')
  const [scopeFilter, setScopeFilter] = useState<'all' | Scope>('all')
  const [categoryFilter, setCategoryFilter] = useState<'all' | DeviceCategory>('all')
  const [brandFilter, setBrandFilter] = useState<'all' | string>('all')
  const [needsChecking, setNeedsChecking] = useState(false)
  const [density, setDensity] = useLocalStorage<Density>(DENSITY_KEY, DEFAULT_DENSITY, densityValue)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const index = useMemo(() => buildSearchIndex(devices), [devices])
  const hits = useMemo(() => {
    const pool = devices.filter((device) => {
      if (scopeFilter !== 'all' && scopeOf(device) !== scopeFilter) return false
      if (categoryFilter !== 'all' && device.category !== categoryFilter) return false
      if (brandFilter !== 'all' && device.manufacturer !== brandFilter) return false
      if (needsChecking && device.verified) return false
      return true
    })
    return searchDevices(pool, index, query)
  }, [devices, index, query, scopeFilter, categoryFilter, brandFilter, needsChecking])
  const filtered = useMemo(() => hits.map((hit) => hit.device), [hits])
  const hints = useMemo(
    () => new Map(hits.flatMap((hit) => (hit.hint ? [[hit.device.id, hit.hint] as const] : []))),
    [hits],
  )
  const searching = query.trim() !== ''
  // Favourites are pinned only while browsing: a search result lists each
  // device once, and the pinned group would otherwise sit beside its category.
  const groups = useMemo(
    () => (searching ? groupByCategory(filtered) : partitionGroups(filtered, { favouriteIds })),
    [filtered, searching, favouriteIds],
  )
  const brandNames = useMemo(() => distinctBrands(devices), [devices])
  const brandCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const device of devices) counts.set(device.manufacturer, (counts.get(device.manufacturer) ?? 0) + 1)
    return counts
  }, [devices])
  // Logos are global, so only manufacturers with catalogue devices get one.
  const catalogueBrands = useMemo(
    () => new Set(devices.filter((device) => device.workspaceId === null).map((device) => device.manufacturer)),
    [devices],
  )
  const categoryCounts = useMemo(() => {
    const counts = new Map<DeviceCategory, number>()
    for (const device of devices) counts.set(device.category, (counts.get(device.category) ?? 0) + 1)
    return counts
  }, [devices])
  const cards = useMemo(() => devices.filter(isCard), [devices])

  const ownCount = devices.filter((device) => device.workspaceId === workspaceId).length
  const unverifiedCount = devices.filter((device) => !device.verified).length
  const selected = devices.find((device) => device.id === selectedId) ?? null

  // On a phone the detail card is below the list, so selecting a row changed
  // something the reader could not see. Only below lg, where they stack.
  const detailRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!selectedId) return
    if (window.matchMedia('(min-width: 1024px)').matches) return
    detailRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    })
  }, [selectedId])
  const filtersActive = searching || categoryFilter !== 'all' || brandFilter !== 'all' || needsChecking
  const compact = density === 'compact'

  function clearFilters() {
    setQuery('')
    setCategoryFilter('all')
    setBrandFilter('all')
    setNeedsChecking(false)
  }

  function upsertLocal(saved: DeviceDTO) {
    setDevices((current) => {
      const exists = current.some((device) => device.id === saved.id)
      const next = exists
        ? current.map((device) => (device.id === saved.id ? saved : device))
        : [...current, saved]
      return next.sort(byBrandNameModel)
    })
    setSelectedId(saved.id)
    setMode({ kind: 'idle' })
    router.refresh()
  }

  function updateBrand(brand: BrandDTO) {
    setBrandRows((current) =>
      current.some((row) => row.name === brand.name)
        ? current.map((row) => (row.name === brand.name ? brand : row))
        : [...current, brand],
    )
  }

  function endpointsFor(scope: Scope) {
    return scope === 'catalogue'
      ? { createUrl: '/api/catalogue', deviceUrl: (id: string) => `/api/catalogue/${id}` }
      : { createUrl: `${apiBase}/devices`, deviceUrl: (id: string) => `${apiBase}/devices/${id}` }
  }

  /** Cards a computer in `scope` may install: the catalogue, plus ours for workspace devices. */
  function cardsFor(scope: Scope, editing: DeviceDTO | null) {
    return cards.filter(
      (candidate) => candidate.id !== editing?.id && (scope === 'catalogue' ? candidate.workspaceId === null : true),
    )
  }

  async function handleDelete(device: DeviceDTO) {
    if (!confirm(`Delete ${device.manufacturer} ${device.model}?`)) return
    setError(null)
    const url =
      scopeOf(device) === 'catalogue' ? `/api/catalogue/${device.id}` : `${apiBase}/devices/${device.id}`
    const response = await fetch(url, { method: 'DELETE' })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      setError(payload.error ?? 'Could not delete device')
      return
    }
    setDevices((current) => current.filter((item) => item.id !== device.id))
    setSelectedId(null)
    setMode({ kind: 'idle' })
    router.refresh()
  }

  async function handleDuplicate(device: DeviceDTO) {
    setError(null)
    setBusy(true)
    try {
      const response = await fetch(`${apiBase}/devices/${device.id}/duplicate`, { method: 'POST' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error ?? 'Could not duplicate device')
      upsertLocal(payload as DeviceDTO)
      setMode({ kind: 'edit', device: payload as DeviceDTO })
    } catch (cause) {
      setError(errorMessage(cause, 'Could not duplicate device'))
    } finally {
      setBusy(false)
    }
  }

  /** One click, no form: the verify endpoints only touch the stamp, never the ports. */
  async function handleVerify(device: DeviceDTO, verified: boolean) {
    if (
      verified &&
      !device.sourceUrl &&
      !confirm(`${device.manufacturer} ${device.model} has no spec page link. Mark it verified anyway?`)
    ) {
      return
    }
    setError(null)
    setBusy(true)
    try {
      const url =
        scopeOf(device) === 'catalogue'
          ? `/api/catalogue/${device.id}/verify`
          : `${apiBase}/devices/${device.id}/verify`
      const response = await fetch(url, { method: verified ? 'POST' : 'DELETE' })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error ?? 'Could not update verification')
      const saved = payload as DeviceDTO
      setDevices((current) => current.map((item) => (item.id === saved.id ? saved : item)))
      router.refresh()
    } catch (cause) {
      setError(errorMessage(cause, 'Could not update verification'))
    } finally {
      setBusy(false)
    }
  }

  async function toggleFavourite(device: DeviceDTO) {
    const on = !favouriteIds.has(device.id)
    const apply = (value: boolean) =>
      setFavouriteIds((current) => {
        const next = new Set(current)
        if (value) next.add(device.id)
        else next.delete(device.id)
        return next
      })
    apply(on)
    const response = await fetch(`${apiBase}/devices/${device.id}/favourite`, { method: on ? 'PUT' : 'DELETE' })
    if (!response.ok) {
      apply(!on)
      const payload = await response.json().catch(() => ({}))
      setError(payload.error ?? 'Could not update favourites')
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-5 py-6 sm:px-8 lg:flex-row lg:py-8">
      {/* List */}
      <aside
        className={cx(
          card,
          'flex w-full flex-col overflow-hidden lg:max-h-[calc(100dvh-4rem)] lg:w-80',
          // Below lg the two stack, so a full-height list pushes the thing you
          // just selected off the bottom of the screen. Give it back some room.
          selected || mode.kind === 'create' ? 'max-h-[40vh] lg:max-h-[calc(100dvh-4rem)]' : 'max-h-[70vh]',
        )}
      >
        <div className="shrink-0 space-y-3 border-b border-[var(--border)] p-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-semibold tracking-tight">Equipment</h1>
            <div className="flex gap-1.5">
              {isPlatformAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(null)
                    setMode({ kind: 'create', scope: 'catalogue' })
                  }}
                  className={cx(btn.sm, btn.secondary)}
                  title="Add to the shared catalogue every workspace sees"
                >
                  + Catalogue
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSelectedId(null)
                  setMode({ kind: 'create', scope: 'workspace' })
                }}
                className={cx(btn.sm, btn.primary)}
              >
                + New
              </button>
            </div>
          </div>
          <div className="relative">
            <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              className={cx(input, 'pl-9')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, make, model, port, IP…"
              aria-label="Search equipment"
            />
          </div>
          <div className="flex items-center gap-1">
            {(
              [
                ['all', 'All', devices.length],
                ['workspace', 'Ours', ownCount],
                ['catalogue', 'Catalogue', devices.length - ownCount],
              ] as const
            ).map(([value, text, count]) => (
              <button
                key={value}
                type="button"
                onClick={() => setScopeFilter(value)}
                aria-pressed={scopeFilter === value}
                className={cx(segment.base, scopeFilter === value ? segment.on : segment.off)}
              >
                {text}
                <span className={cx('font-mono text-[10px]', scopeFilter === value ? 'text-on-ink/70' : 'text-muted/70')}>
                  {count}
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setDensity(compact ? 'comfortable' : 'compact')}
              aria-pressed={compact}
              aria-label="Compact rows"
              title={compact ? 'Comfortable rows' : 'Compact rows'}
              className={cx(segment.base, 'ml-auto', compact ? segment.on : segment.off)}
            >
              <Icon name="density" className="h-3.5 w-3.5" />
            </button>
          </div>
          {/* Brand and category are the two long single-choice filters, so they
              share a row as two matching controls. The verification toggle is
              an Advanced-mode concern and sits on its own line beneath. */}
          <div className="grid grid-cols-2 gap-2">
            <BrandPicker
              brands={brandRows}
              names={brandNames}
              counts={brandCounts}
              total={devices.length}
              selected={brandFilter}
              onSelect={setBrandFilter}
              canManage={(name) => isPlatformAdmin && catalogueBrands.has(name)}
              onBrandChange={updateBrand}
              onError={setError}
            />
            <select
              aria-label="Filter by category"
              className={cx(inputBase, 'w-full min-w-0')}
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value as 'all' | DeviceCategory)}
            >
              <option value="all">All categories</option>
              {DEVICE_CATEGORIES.filter((category) => categoryCounts.get(category)).map((category) => (
                <option key={category} value={category}>
                  {CATEGORY_META[category].label} ({categoryCounts.get(category)})
                </option>
              ))}
            </select>
          </div>
          {advanced && (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setNeedsChecking((value) => !value)}
                aria-pressed={needsChecking}
                title="Only devices whose ports have not been checked against a spec sheet"
                className={cx(segment.base, needsChecking ? segment.on : segment.off)}
              >
                Needs checking
                <span className={cx('font-mono text-[10px]', needsChecking ? 'text-on-ink/70' : 'text-muted/70')}>
                  {unverifiedCount}
                </span>
              </button>
            </div>
          )}
        </div>

        <ul className="min-h-0 flex-1 overflow-y-auto p-2">
          {groups.map(([group, items]) => (
            <li key={group}>
              <DeviceGroupHeader group={group} count={items.length} surface="card" />
              <ul>
                {items.map((device) => (
                  <LibraryRow
                    key={device.id}
                    device={device}
                    active={device.id === selectedId && mode.kind !== 'create'}
                    owned={unitCounts[device.id] ?? 0}
                    hint={hints.get(device.id) ?? null}
                    favourite={favouriteIds.has(device.id)}
                    compact={compact}
                    onSelect={() => {
                      setSelectedId(device.id)
                      setMode({ kind: 'idle' })
                    }}
                    onToggleFavourite={() => toggleFavourite(device)}
                  />
                ))}
              </ul>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-3 py-8 text-center text-xs text-muted">
              {filtersActive ? 'Nothing matches the current filters.' : 'Nothing here yet.'}
              {filtersActive && (
                <button type="button" onClick={clearFilters} className={cx(btn.sm, btn.ghost, 'mt-2')}>
                  Clear filters
                </button>
              )}
            </li>
          )}
        </ul>
      </aside>

      {/* Detail / editor */}
      <section
        ref={detailRef}
        className={cx(card, 'flex min-h-[420px] flex-1 flex-col overflow-hidden lg:max-h-[calc(100dvh-4rem)]')}
      >
        {error && <p role="alert" className={cx('m-4', notice.error)}>{error}</p>}

        {mode.kind === 'create' && (
          <DeviceEditor
            device={null}
            {...endpointsFor(mode.scope)}
            cards={cardsFor(mode.scope, null)}
            onSaved={upsertLocal}
            onCancel={() => setMode({ kind: 'idle' })}
          />
        )}

        {mode.kind === 'edit' && (
          <DeviceEditor
            key={mode.device.id}
            device={mode.device}
            {...endpointsFor(scopeOf(mode.device))}
            cards={cardsFor(scopeOf(mode.device), mode.device)}
            onSaved={upsertLocal}
            onCancel={() => setMode({ kind: 'idle' })}
          />
        )}

        {mode.kind === 'idle' && selected && (
          <DeviceDetail
            device={selected}
            owned={unitCounts[selected.id] ?? 0}
            inventoryPath={inventoryPath}
            editable={canEdit(selected)}
            busy={busy}
            onEdit={() => setMode({ kind: 'edit', device: selected })}
            onDuplicate={() => handleDuplicate(selected)}
            onDelete={() => handleDelete(selected)}
            onVerify={(verified) => handleVerify(selected, verified)}
          />
        )}

        {mode.kind === 'idle' && !selected && (
          <div className="grid flex-1 place-items-center p-8 text-center">
            <div>
              <span className="gradient-pastel mx-auto grid h-12 w-12 place-items-center rounded-2xl text-foreground/60 shadow-[var(--shadow-card)]">
                <Icon name="equipment" className="h-6 w-6" />
              </span>
              <p className="mx-auto mt-4 max-w-sm text-sm text-muted">
                Select a device to view its ports, or create a new one. The catalogue is shared by
                every workspace; devices you create here belong to yours.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

/**
 * One list row: the device button plus a sibling star. Siblings, never nested
 * — a button inside a button is invalid markup and React logs it.
 */
function LibraryRow({
  device,
  active,
  owned,
  hint,
  favourite,
  compact,
  onSelect,
  onToggleFavourite,
}: {
  device: DeviceDTO
  active: boolean
  owned: number
  /** "SDI In 12 · BNC (SDI)" when the row only matched through a port. */
  hint: string | null
  favourite: boolean
  compact: boolean
  onSelect: () => void
  onToggleFavourite: () => void
}) {
  const { inputs, outputs, bidirectional } = splitPorts(device.ports)
  const unverifiedDot = !device.verified && (
    <span
      title="Ports not yet checked against a manufacturer spec sheet"
      role="img"
      aria-label="Unverified: ports not yet checked against a manufacturer spec sheet"
      className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning"
    />
  )
  return (
    <li className="flex items-center gap-0.5 pr-1">
      <button
        type="button"
        onClick={onSelect}
        className={cx(
          'min-w-0 flex-1 rounded-xl px-3 text-left transition-colors',
          compact ? 'py-1.5' : 'py-2.5',
          active
            ? 'bg-[var(--surface-raised)] ring-1 ring-inset ring-[var(--border-strong)]'
            : 'hover:bg-hover',
        )}
      >
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <span className="truncate">{device.model}</span>
          {owned > 0 && (
            <span className={cx(tag, tagTone.success, 'shrink-0 font-mono')}>×{owned}</span>
          )}
          {compact && unverifiedDot}
        </p>
        <p className="truncate text-xs text-muted">
          {device.manufacturer} · {device.name}
        </p>
        {!compact && (
          <p className="mt-1 flex items-center gap-1.5 font-mono text-[10px] text-muted">
            {unverifiedDot}
            <span>
              {metaLine(
                formatFormFactor(device.formFactorRU),
                `${inputs.length} in`,
                `${outputs.length} out`,
                bidirectional.length > 0 && `${bidirectional.length} bidi`,
              )}
            </span>
            {scopeOf(device) === 'catalogue' && <span className="ml-auto text-muted/60">catalogue</span>}
          </p>
        )}
        {/* Hidden from the accessible name so a role query by model keeps matching one row. */}
        {hint && (
          <p aria-hidden="true" className="mt-0.5 truncate font-mono text-[10px] text-accent">
            matches: {hint}
          </p>
        )}
      </button>
      <button
        type="button"
        onClick={onToggleFavourite}
        aria-pressed={favourite}
        aria-label={`${favourite ? 'Unfavourite' : 'Favourite'} ${device.model}`}
        title={favourite ? 'Remove from favourites' : 'Add to favourites'}
        className={cx(starBtn.base, favourite ? starBtn.on : starBtn.off)}
      >
        <Icon name={favourite ? 'starFilled' : 'star'} className="h-4 w-4" />
      </button>
    </li>
  )
}

function DeviceDetail({
  device,
  owned,
  inventoryPath,
  editable,
  busy,
  onEdit,
  onDuplicate,
  onDelete,
  onVerify,
}: {
  device: DeviceDTO
  owned: number
  inventoryPath: string
  editable: boolean
  busy: boolean
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  onVerify: (verified: boolean) => void
}) {
  const { advanced } = useUiMode()
  const { onboard, bySlot } = partitionPorts(device)
  const { inputs, outputs, bidirectional } = splitPorts(onboard)
  const isCatalogue = scopeOf(device) === 'catalogue'
  const isComputer = device.category === 'COMPUTER'
  const filledSlots = device.slots.filter((slot) => slot.card).length

  const counts = SIGNAL_CATEGORIES.map((signal) => ({
    signal,
    count: device.ports.filter((port) => port.signal === signal).length,
  })).filter((entry) => entry.count > 0)

  const specs = (
    [
      ['CPU', device.cpu],
      ['RAM', device.ram],
      ['GPU', device.gpu],
      ['Storage', device.storage],
      ['OS', device.os],
    ] as const
  ).filter((entry) => entry[1])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="gradient-hero shrink-0 border-b border-[var(--border)] p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold tracking-tight">{device.name}</h2>
              {/* Whether a spec sheet has been checked matters to whoever
                  curates the catalogue. It is noise to someone picking a
                  camera. */}
              {advanced && (
                <span className={cx(tag, device.verified ? tagTone.success : tagTone.warning)}>
                  {device.verified ? 'Verified' : 'Unverified'}
                </span>
              )}
              {advanced && (
                <span className={cx(tag, tagTone.neutral)}>
                  {isCatalogue ? 'Shared catalogue' : 'Our device'}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-muted">{describeDevice(device.manufacturer, device.model)}</p>
            {advanced && (
              <p className={cx('mt-1 text-xs', device.verified ? 'text-success' : 'text-warning')}>
                {verificationSummary(device)}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {device.sourceUrl && (
              <a
                href={device.sourceUrl}
                target="_blank"
                rel="noreferrer noopener"
                className={cx(btn.base, btn.secondary)}
                title={device.sourceUrl}
              >
                {advanced ? 'Open spec page' : "Manufacturer's page"}
                <Icon name="external" className="h-3.5 w-3.5" />
              </a>
            )}
            {isCatalogue && (
              <button
                type="button"
                onClick={onDuplicate}
                disabled={busy}
                className={cx(btn.base, btn.secondary)}
                title="Make an editable copy in your workspace"
              >
                <Icon name="copy" className="h-4 w-4" />
                Duplicate to ours
              </button>
            )}
            {editable && (
              <>
                {device.verified ? (
                  <button
                    type="button"
                    onClick={() => onVerify(false)}
                    disabled={busy}
                    className={cx(btn.base, btn.ghost)}
                    title="Clear the verification stamp"
                  >
                    Unverify
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onVerify(true)}
                    disabled={busy}
                    className={cx(btn.base, btn.primary)}
                    title="Record that you checked every port against the spec page"
                  >
                    <Icon name="check" className="h-4 w-4" />
                    Mark verified
                  </button>
                )}
                <button type="button" onClick={onEdit} className={cx(btn.base, btn.secondary)}>
                  Edit
                </button>
                <button type="button" onClick={onDelete} className={cx(btn.base, btn.danger)}>
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        <dl className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
          <Fact label="Category" value={CATEGORY_META[device.category].singular} />
          <Fact label="Brand" value={device.manufacturer} />
          {formatFormFactor(device.formFactorRU) && (
            <Fact label="Form factor" value={formatFormFactor(device.formFactorRU) as string} />
          )}
          {device.ipAddress && <Fact label="IP" value={device.ipAddress} />}
          <Fact label="Ports" value={`${device.ports.filter((p) => p.direction === 'INPUT').length} in / ${device.ports.filter((p) => p.direction === 'OUTPUT').length} out`} />
          {isComputer && (
            <Fact
              label="Slots"
              value={device.slots.length === 0 ? 'None' : `${filledSlots} of ${device.slots.length} filled`}
            />
          )}
          {specs.map(([name, value]) => (
            <Fact key={name} label={name} value={value} />
          ))}
          {device.installedInCount > 0 && (
            <Fact
              label="Installed in"
              value={`${device.installedInCount} computer${device.installedInCount === 1 ? '' : 's'}`}
            />
          )}
          <Fact
            label="In inventory"
            value={
              <>
                {owned} unit{owned === 1 ? '' : 's'}{' '}
                <Link href={inventoryPath} className="text-accent underline underline-offset-2">
                  {owned > 0 ? 'view' : 'add'}
                </Link>
              </>
            }
          />
        </dl>

        {counts.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {counts.map(({ signal, count }) => (
              <span key={signal} className="inline-flex items-center gap-1">
                <SignalChip signal={signal} />
                <span className="font-mono text-[10px] text-muted">×{count}</span>
              </span>
            ))}
          </div>
        )}

        {device.notes && <p className="mt-3 text-sm text-muted">{device.notes}</p>}

        {!device.sourceUrl && editable && (
          <p className="mt-2 text-xs text-muted/80">
            No spec page recorded. Add one in Edit so the next person can check the ports too.
          </p>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <PortColumn title="Inputs" ports={inputs} />
          <PortColumn title="Outputs" ports={outputs} />
        </div>
        {bidirectional.length > 0 && (
          <div className="mt-5">
            <PortColumn title="Bi-directional" ports={bidirectional} />
          </div>
        )}

        {device.slots.length > 0 && (
          <div className="mt-6 space-y-4">
            {device.slots.map((slot) => {
              const slotPorts = splitPorts(bySlot.get(slot.id) ?? [])
              return (
                <section key={slot.id} className="rounded-2xl border border-[var(--border)] p-4">
                  <h3 className="text-sm font-semibold">
                    {slotDescriptor(slot.label, slot.card ? describeDevice(slot.card.manufacturer, slot.card.model) : null)}
                    <span className="font-normal text-muted"> · {SLOT_BUS_LABELS[slot.bus]}{slot.card ? '' : ' · Empty'}</span>
                  </h3>
                  {slot.card && (
                    <div className="mt-3 grid gap-5 sm:grid-cols-2">
                      <PortColumn title="Inputs" ports={slotPorts.inputs} />
                      <PortColumn title="Outputs" ports={slotPorts.outputs} />
                    </div>
                  )}
                  {slotPorts.bidirectional.length > 0 && (
                    <div className="mt-4">
                      <PortColumn title="Bi-directional" ports={slotPorts.bidirectional} />
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        )}
        {isComputer && device.slots.length === 0 && (
          <p className="mt-6 text-xs text-muted">No slots defined. Edit the computer to add PCIe or Thunderbolt slots.</p>
        )}
      </div>
    </div>
  )
}

/**
 * One fact about a device. Plain text on purpose: a row of eight key:value
 * capsules reads as eight buttons nobody can press, and the spacing does the
 * separating a background was doing badly.
 */
function Fact({ label: text, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-[11px]">
      <dt className="inline text-muted">{text} </dt>
      <dd className="inline font-medium text-foreground">{value}</dd>
    </div>
  )
}

function PortColumn({ title, ports }: { title: string; ports: DeviceDTO['ports'] }) {
  return (
    <div>
      <h3 className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">
        {title} ({ports.length})
      </h3>
      {ports.length === 0 ? (
        <p className="mt-2 text-xs text-muted/70">None</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {ports.map((port) => (
            <li
              key={port.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface-raised)] px-3 py-2"
            >
              <span className="truncate text-xs font-medium">{port.name}</span>
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="font-mono text-[10px] text-muted">{port.connector}</span>
                <SignalChip signal={port.signal} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
